import {
  getStudents,
  getStudentResources,
  getSharedStudent,
  getRecentStudents,
  deleteStudentResource,
  getAllResourcesForContentTypes
} from '@/api/library.js'

import { CONTENT_TYPES } from '@/util/access_visibility'
import { LibraryFolderMembersList } from '@/util/library_folder_members'
import { LibraryPermissionValidator } from '@/util/library_permission_validator'

export default {
  namespaced: true,
  state: {
    students: [],
    recentStudents: [],
    loadingStudents: true,
    _studentsLoadedCallbackQueue: [],

    currentStudent: {},
    loadingResources: true,
    goals: [],
    strategies: [],
    presentlevels: [],
    resources: [],

    currentContentFolder: {
      folderUuid: '',
      folderName: '',
      folderUsers: new LibraryFolderMembersList(),
      folderPermissions: new LibraryPermissionValidator(),
      contentUuid: '',
      contentContributorUUID: '',
      contentType: '',
    },

    filters: {
      goals: 'All Subjects',
      strategies: 'All Subjects',
      presentlevel: 'All Subjects'
    }
  },
  mutations: {
    setGoals(state, goals) {
      state.goals = goals
    },
    setStrategies(state, strats) {
      state.strategies = strats
    },
    setPresentLevels(state, presentlevels) {
      state.presentlevels = presentlevels
    },
    setResources(state, resources) {
      state.resources = resources
    },
    setStudents(state, students) {
      students.forEach(student => {
        student.folderMembers = new LibraryFolderMembersList(student.folder_roles)
      })
      state.students = students
    },
    setRecentStudents(state, students) {
      state.recentStudents = students
    },
    setStudentsLoading(state, bool) {
      state.loadingStudents = bool
    },
    pushStudentsLoadedCallback(state, callback) {
      state._studentsLoadedCallbackQueue.push(callback)
    },
    clearStudentsLoadedCallbackQueue(state) {
      state._studentsLoadedCallbackQueue = []
    },
    setResourcesLoading(state, bool) {
      state.loadingResources = bool
    },
    setCurrentStudent(state, student) {
      state.currentStudent = student
    },
    setFilter(state, info) {
      state.filters[info.type] = info.filter
    },
    resetFilters(state) {
      state.filters = {
        goals: 'All Subjects',
        strategies: 'All Subjects',
        presentlevel: 'All Subjects'
      }
    },
    removeResource(state, info) {
      if (info.type === CONTENT_TYPES.SAVED_STRATEGY) {
        state.strategies = state.strategies.filter(strat => {
          return strat.content_uuid !== info.uuid
        })
      } else if (
        info.type === CONTENT_TYPES.SAVED_GOAL ||
        info.type === CONTENT_TYPES.SAVED_SUPER_PAGE
      ) {
        state.goals = state.goals.filter(goal => {
          return goal.content_uuid !== info.uuid
        })
      } else if (info.type === CONTENT_TYPES.PRESENTLEVEL) {
        state.presentlevels = state.presentlevels.filter(pl => {
          return pl.content_uuid !== info.uuid
        })
      } else if (info.type === CONTENT_TYPES.RESOURCE) {
        state.resources = state.resources.filter(r => {
          return r.content_uuid !== info.uuid
        })
      }
    },
    setCurrentContentFolder(state, folderInfo) {
      state.currentContentFolder.folderUuid = folderInfo.uuid ? folderInfo.uuid : ''
      state.currentContentFolder.folderName = folderInfo.name ? folderInfo.name : ''
      state.currentContentFolder.folderUsers = new LibraryFolderMembersList(folderInfo.users)
      state.currentContentFolder.folderPermissions = new LibraryPermissionValidator(folderInfo.permissions)
      state.currentContentFolder.contentUuid = folderInfo.contentUuid
      state.currentContentFolder.contentContributorUUID = folderInfo.contentContributorUUID
      state.currentContentFolder.contentType = folderInfo.contentType
    },
    clearCurrentContentFolder(state) {
      state.currentContentFolder = {
        folderUuid: '',
        folderName: '',
        folderUsers: new LibraryFolderMembersList(),
        folderPermissions: new LibraryPermissionValidator(),
        contentUuid: '',
        contentContributorUUID: '',
        contentType: '',
      }
    }
  },
  actions: {
    fetchStudents(context, userId) {
      context.commit('setStudentsLoading', true)
      return getStudents(userId)
          .then(data => {
            context.commit('setStudents', data.collections)
          })
          .catch(err => {
            console.error('fetchStudents failed', err)
          })
        .finally(() => {
          context.commit('setStudentsLoading', false)
          context.state._studentsLoadedCallbackQueue.forEach((cb) => {
            try { cb() } catch (cbErr) { console.error('studentsLoadedCallback error', cbErr) }
          })
          context.commit('clearStudentsLoadedCallbackQueue')
        })
    },
    fetchRecentStudents(context, { userId, includeShared = false }) {
      return getRecentStudents(userId, includeShared).then(data => {
          context.commit('setRecentStudents', data)
        })
    },
    fetchResourcesForStudent(context, { studentId, params }) {
      context.commit('setResourcesLoading', true)
      return getStudentResources(studentId, params).then(data => {
        setResourcesForStudent(context, data)
        context.commit('setResourcesLoading', false)
        return data
      }).catch(e => console.error(e))
    },
    fetchResourcesForSharedStudent(context, { shareToken, params }) {
      context.commit('setResourcesLoading', true)
      return getSharedStudent(shareToken, params).then(data => {
        setResourcesForStudent(context, data)
        context.commit('setResourcesLoading', false)
        return data
      })
    },
    setFilter(context, info) {
      return context.commit('setFilter', info)
    },
    deleteResource(context, info) {
      return deleteStudentResource(info.collection, info.uuid, info.type).then(() => {
        context.commit('removeResource', info)
      })
    },
    async fetchCurrentContentFolder({ commit, rootState, dispatch }, libraryParams) {
      const { contentUuid, userUuid, contentType } = libraryParams
      if (!contentUuid || !userUuid || !contentType) {
        console.error(`fetchCurrentContentFolder missing parameters. contentUuid: ${contentUuid}, userUuid: ${userUuid}, contentType: ${contentType}`)
        commit('clearCurrentContentFolder')
        return
      }

      let savedContent = []
      try {
        const response = await getAllResourcesForContentTypes(userUuid, [contentType], true)
        if (!response || !Array.isArray(response.content) || response.content.length === 0) {
          commit('clearCurrentContentFolder')
          return
        }
        savedContent = response.content.filter(c => c.content_uuid === contentUuid)
        if (savedContent.length === 0) {
          commit('clearCurrentContentFolder')
          return
        }
      } catch (e) {
        console.error('fetchCurrentContentFolder: failed to load resources', e)
        commit('clearCurrentContentFolder')
        return
      }

      const processLibraryData = () => {
        const libraryFolder = rootState.library.students.find(libraryFolder => libraryFolder.uuid === savedContent[0].collection_body.uuid)
        if (!libraryFolder) {
          console.error('no folder found with uuid corresponding to saved content')
          commit('clearCurrentContentFolder')
          return
        }

        const users = (Array.isArray(libraryFolder?.folder_roles) ? libraryFolder.folder_roles : [])
          .map(({ user_uuid, user_name, role, user_email }) => ({
            uuid: user_uuid,
            name: user_name || '',
            role,
            email: user_email || ''
          }))
        commit('setCurrentContentFolder', {
          uuid: savedContent[0].collection_body.uuid,
          name: savedContent[0].collection_body.name,
          users,
          permissions: Array.isArray(libraryFolder?.permissions) ? libraryFolder.permissions : [],
          contentUuid: savedContent[0].content_uuid,
          contentContributorUUID: savedContent[0].contributor,
          contentType: savedContent[0].content_type,
        })
      }

      if (rootState.library.loadingStudents) {
        // If still loading library folders, register a callback
        commit('pushStudentsLoadedCallback', processLibraryData)
      } else {
        processLibraryData()
      }
    },

  },
  getters: {
    getUserRoleForFolder: (state, getters, rootState) => (folderUuid) => {
      if (!folderUuid || !rootState.user?.user?.uuid) return undefined

      const folder = state.students.find(student => student.uuid === folderUuid)
      if (!folder?.folder_roles) return undefined

      const userRole = folder.folder_roles.find(
        role => role.user_uuid === rootState.user.user.uuid
      )

      return userRole?.role
    },
    currentUserRoleForCurrentFolder(state, getters) {
      const folderUuid = getters.currentContentFolderUuid
      if (!folderUuid) return undefined
      return getters.getUserRoleForFolder(folderUuid)
    },
    isUserRoleResolvedForCurrentFolder(state, getters) {
      const folderUuid = getters.currentContentFolderUuid
      if (!folderUuid) return false
      return getters.currentUserRoleForCurrentFolder !== undefined
    },
    isCurrentUserContributorForCurrentFolder(state, getters) {
      return getters.currentUserRoleForCurrentFolder === 'contributor'
    },
    currentStudentContentCount(state) {
      return (
        state.goals.length +
        state.strategies.length +
        state.presentlevels.length +
        state.resources.length
      )
    },
    folderCount(state) {
      return (state.students || []).length
    },
    userOwnedFolders(state, getters, rootState, rootGetters) {
      const userId = rootGetters['user/userId']
      return state.students.filter(folder => folder.owner === userId)
    },
    sharedFolders(state, getters, rootState, rootGetters) {
      const userId = rootGetters['user/userId']
      return state.students.filter(folder => folder.owner !== userId)
    },
    libraryFolderOwner(state) {
      return state.currentContentFolder.folderUsers.getFolderOwner()
    },
    libraryFolderMembers(state) {
      return state.currentContentFolder.folderUsers.getFolderMembers()
    },
    isLibraryFolderShared(state) {
      const members = state.currentContentFolder.folderUsers.getFolderMembers()
      return members.length > 1
    },
    currentContentFolderUuid(state) {
      if (!state.currentContentFolder) {
        return ''
      }
      return state.currentContentFolder.folderUuid
    },
    currentContentFolderName(state) {
      if (!state.currentContentFolder) {
        return ''
      }
      return state.currentContentFolder.folderName
    },
    currentContentUuid(state) {
      if (!state.currentContentFolder) {
        return ''
      }
      return state.currentContentFolder.contentUuid
    },
    canEditCurrentContent(state) {
      return state.currentContentFolder.folderPermissions.canEdit()
    },
    canRemoveCurrentContent(state) {
      return state.currentContentFolder.folderPermissions.canRemove()
    },
    canEditCurrentTextarea(state, getters, rootState, rootGetters) {
      const currentContentType = getters.currentContentType
      if (currentContentType === '') {
        return true
      } else {
        return getters.canEditCurrentContent
      }
    },
    currentContentType(state) {
      if (!state.currentContentFolder) {
        return ''
      }
      return state.currentContentFolder.contentType
    }
  }
}

function setResourcesForStudent(context, { collection }) {
  context.commit('setCurrentStudent', collection)
  let goals = []
  let strategies = []
  let presentlevels = []
  let resources = []

  if (collection.content) {
    collection.content.forEach(function(resource) {
      if (resource.content_body_error === 'not found' || !resource.content_body) {
        return
      }
      if (
        resource.content_type === CONTENT_TYPES.SAVED_GOAL ||
        resource.content_type === CONTENT_TYPES.SAVED_SUPER_PAGE
      ) {
        goals.push(resource)
      } else if (resource.content_type === CONTENT_TYPES.SAVED_STRATEGY) {
        strategies.push(resource)
      } else if (resource.content_type === CONTENT_TYPES.PRESENTLEVEL) {
        presentlevels.push(resource)
      } else if (resource.content_type === CONTENT_TYPES.RESOURCE) {
        resources.push(resource)
      }
    })
  }
  context.commit('setStrategies', strategies)
  context.commit('setGoals', goals)
  context.commit('setPresentLevels', presentlevels)
  context.commit('setResources', resources)
}
